// client/src/components/auth/SessionTimeout.js - TRADOTTO
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import tokenService from '../../services/tokenService';

const SessionTimeout = ({ timeoutMinutes = 15, warningMinutes = 1 }) => {
  const navigate = useNavigate();
  const { logout, isAuthenticated, user } = useAuth();
  const { t } = useTranslation();
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(warningMinutes * 60);
  
  // References for timers
  const warningTimeoutId = useRef(null);
  const logoutTimeoutId = useRef(null);
  const countdownIntervalId = useRef(null);
  const checkIntervalId = useRef(null);
  
  // FLAG per prevenire logout multipli
  const isLoggingOut = useRef(false);
  
  // Format remaining time as mm:ss
  const formatTimeLeft = () => {
    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft % 60;
    return `${minutes}:${seconds < 10 ? '0' + seconds : seconds}`;
  };

  // Clear all timers
  const clearAllTimers = () => {
    if (warningTimeoutId.current) clearTimeout(warningTimeoutId.current);
    if (logoutTimeoutId.current) clearTimeout(logoutTimeoutId.current);
    if (countdownIntervalId.current) clearInterval(countdownIntervalId.current);
    if (checkIntervalId.current) clearInterval(checkIntervalId.current);
  };

  // Handle logout - CORRETTO per prevenire chiamate multiple
  const handleLogout = useCallback(() => {
    // Prevenire logout multipli
    if (isLoggingOut.current) {
      return;
    }
    
    isLoggingOut.current = true;
    
    clearAllTimers();
    setShowWarning(false);
    
    // Chiamare logout UNA SOLA VOLTA
    try {
      logout();
      navigate('/login', { 
        state: { message: t('auth.sessionTimeout.sessionExpired') } 
      });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('SessionTimeout logout error:', error);
      }
    }
    
    // Reset flag dopo delay
    setTimeout(() => {
      isLoggingOut.current = false;
    }, 1000);
  }, [logout, navigate, t]);

  // Start inactivity monitoring - MIGLIORATO
  const startInactivityCheck = useCallback(() => {
    // Non avviare se già in logout
    if (isLoggingOut.current) {
      return;
    }
    
    // Clear any existing check interval
    if (checkIntervalId.current) {
      clearInterval(checkIntervalId.current);
    }
    
    // Set timer for warning (timeoutMinutes - warningMinutes)
    const warningTime = (timeoutMinutes - warningMinutes) * 60 * 1000;
    
    warningTimeoutId.current = setTimeout(() => {
      // Non mostrare warning se già in logout
      if (isLoggingOut.current) {
        return;
      }
      
      // Show warning dialog
      setShowWarning(true);
      setTimeLeft(warningMinutes * 60);
      
      // Start countdown
      countdownIntervalId.current = setInterval(() => {
        setTimeLeft(prevTime => {
          if (prevTime <= 1) {
            clearInterval(countdownIntervalId.current);
            if (!isLoggingOut.current) {
              handleLogout();
            }
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
      
      // Set timer for automatic logout after warning period
      logoutTimeoutId.current = setTimeout(() => {
        if (!isLoggingOut.current) {
          handleLogout();
        }
      }, warningMinutes * 60 * 1000);
    }, warningTime);
  }, [timeoutMinutes, warningMinutes, handleLogout]);

  // Reset inactivity timers - CORRETTO
  const resetTimers = useCallback(() => {
    // Non resettare se in logout
    if (isLoggingOut.current) {
      return;
    }
    
    clearAllTimers();
    setShowWarning(false);
    
    // Update activity time in token service
    tokenService.updateActivity();
    
    // Start fresh inactivity check
    startInactivityCheck();
  }, [startInactivityCheck]);

  // Continue session
  const handleContinueSession = () => {
    if (!isLoggingOut.current) {
      resetTimers();
    }
  };

  // Function to handle activity while warning is shown
  const handleActivityDuringWarning = useCallback(() => {
    // Only reset if warning is currently shown and not logging out
    if (showWarning && !isLoggingOut.current) {
      resetTimers();
    }
  }, [showWarning, resetTimers]);

  // Effect to monitor user activity - CORRETTO
  useEffect(() => {
    // Only proceed if user is authenticated and not logging out
    if (isAuthenticated && user && !isLoggingOut.current) {
      // Initialize activity time
      tokenService.updateActivity();
      
      // Start inactivity monitoring
      startInactivityCheck();
      
      // Only add listeners for dismissing warning when it's shown
      if (showWarning) {
        const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
        
        activityEvents.forEach(event => {
          window.addEventListener(event, handleActivityDuringWarning, true);
        });
        
        return () => {
          activityEvents.forEach(event => {
            window.removeEventListener(event, handleActivityDuringWarning, true);
          });
        };
      }
    } else {
      // Clear timers if user is not authenticated
      clearAllTimers();
      setShowWarning(false);
    }

    // Cleanup on unmount
    return () => {
      clearAllTimers();
    };
  }, [isAuthenticated, user, timeoutMinutes, warningMinutes, showWarning, startInactivityCheck, handleActivityDuringWarning]);

  // Don't render anything if no warning is shown or if logging out
  if (!showWarning || isLoggingOut.current) return null;
  
  // Render warning dialog
  return (
    <div className="modal-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000
    }}>
      <div className="modal" style={{
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '8px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        maxWidth: '400px',
        width: '90%'
      }}>
        <div className="modal-header" style={{ marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, color: '#d32f2f' }}>{t('auth.sessionTimeout.title')}</h3>
        </div>
        <div className="modal-body" style={{ marginBottom: '1.5rem' }}>
          <p style={{ margin: '0 0 1rem 0' }}>{t('auth.sessionTimeout.message')}</p>
          <p style={{ margin: 0, fontSize: '1.1rem' }}>
            {t('auth.sessionTimeout.timeLeft')} <strong style={{ color: '#d32f2f' }}>{formatTimeLeft()}</strong>
          </p>
        </div>
        <div className="modal-footer" style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
          <button 
            className="btn btn-secondary"
            onClick={() => !isLoggingOut.current && handleLogout()}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #ccc',
              backgroundColor: '#f5f5f5',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            disabled={isLoggingOut.current}
          >
            {t('auth.sessionTimeout.logoutNow')}
          </button>
          <button 
            className="btn btn-primary"
            onClick={handleContinueSession}
            style={{
              padding: '0.5rem 1rem',
              border: 'none',
              backgroundColor: '#1976d2',
              color: 'white',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            disabled={isLoggingOut.current}
          >
            {t('auth.sessionTimeout.continueSession')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeout;